import React from 'react';
import { connect } from 'react-redux';
import { compose, branch } from 'recompose';
import PropTypes from 'prop-types';
import { SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';

import { translator } from 'mage-i18n';
import { HorizontalWrapper, defaultContainerPropTypes } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS, FLOW_STEP_PATHS, APP_PATHS, REGIONS } from '@/constants';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import {
  canSubmitApplication,
  hasControlOverManagementStakeholders,
  hasDocuments,
  isApplicant,
  isApplicantOnly,
  isEntityTypePartnership,
  isMerchantRegistered,
  isSoleTraderOrSoleProprietorship,
} from '@/flow/configs/selectors';
import {
  getDataForApplicationConfirmation,
  getMerchantAwareTranslator,
  getPricingAnnex,
  merchantRegion,
} from '@/selectors/merchant';
import { isManagedKyc, isUnmanagedKyc } from '@/selectors/widget';
import { logEvent } from '@/utils/analytics';
import {
  AddressSummary,
  BackButton,
  BankAccountsSummary,
  BusinessDetailsSummary,
  CallToActionButton,
  ContainerTemplate,
  DocumentsSummary,
  ProductPricesCard,
  StakeholderSummary,
  SummarySection,
} from '@/components';
import getStakeholderUrlToRedirect from '@/utils/getStakeholderUrlToRedirect';

import { CY_APPLICATION_CONFIRMATION_CONTAINER_IDS } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';

const { BTN_SUBMIT, STAKEHOLDER_BLOCK } = CY_APPLICATION_CONFIRMATION_CONTAINER_IDS;
const tPrefix = 'products-fe.containers.application_summary';

const analyticLogger = () => logEvent(ANALYTICS_EVENTS.applicationConfirmation);
const editLogger = section => logEvent(ANALYTICS_EVENTS.editInformaiton, { section });

export class ApplicationConfirmation extends React.Component {
  constructor(props) {
    super(props);

    const { isUnmanagedKyc, pricingAnnex, merchantData } = props;

    this.state = {
      isTermsAccepted: !isUnmanagedKyc,
      isPricingAnnexAccepted:
        !isUnmanagedKyc || !pricingAnnex?.[merchantData?.productsForAcceptance[0]?.id],
      ctaClicked: false,
    };
    this.productContainerRef = React.createRef();

    this.onCheckTermsAndConditions = this.onCheckTermsAndConditions.bind(this);
    this.onCheckPricingAnnex = this.onCheckPricingAnnex.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  onCheckTermsAndConditions() {
    this.setState({
      ...this.state,
      isTermsAccepted: !this.state.isTermsAccepted,
      ctaClicked: false,
    });
  }

  onCheckPricingAnnex() {
    this.setState({
      ...this.state,
      isPricingAnnexAccepted: !this.state.isPricingAnnexAccepted,
      ctaClicked: false,
    });
  }

  onNext() {
    const { isTermsAccepted, isPricingAnnexAccepted } = this.state;

    if (!isTermsAccepted || !isPricingAnnexAccepted) {
      this.setState({ ctaClicked: true });
    } else {
      const { mid, merchantData, canSubmitApplication, pricingAnnex } = this.props;

      // if application data is not full, merchant will be redirected to the missing step
      if (!canSubmitApplication) {
        this.props.initFlow();
      } else {
        this.props.onNext({
          scrollBeforeNext: true,
          payload: {
            params: {
              mid,
            },
            body: {
              tocLinks: merchantData.productsForAcceptance.map(p => ({
                id: p.id,
                tocLink: p.termsAndConditionsLink,
                ...(pricingAnnex?.[p.id] ? { pricingAnnexLink: pricingAnnex[p.id] } : {}),
              })),
            },
          },
        });
      }
    }
  }

  render() {
    const {
      goToBankAccounts,
      goToCompanyDetails,
      goToDocumentsUpload,
      goToOperatingAddress,
      goToProductSelection,
      goToStakeholders,
      goToStoreDetails,
      hasBack,
      hasDocuments,
      isDisabled,
      isLoading,
      isUnmanagedKyc,
      merchantData,
      openPricing,
      region,
      t,
      tMerchantAware,
      isMerchantRegistered,
      isEntityTypePartnership,
      isSoleTraderOrSoleProprietorship,
      onBack,
      error,
      pricingAnnex,
    } = this.props;
    const {
      address = {},
      bankAccounts = [],
      country,
      operatingAddress = null,
      businessDetails = {},
      stakeholders = [],
      productsForAcceptance = [],
    } = merchantData;
    const { companyName = undefined } = businessDetails;
    const { isTermsAccepted, isPricingAnnexAccepted, ctaClicked } = this.state;

    const hasPricingAnnex = pricingAnnex?.[merchantData?.productsForAcceptance[0]?.id];
    let errorText = null;
    if ((!isTermsAccepted || !isPricingAnnexAccepted) && ctaClicked) {
      errorText = t(
        `${tPrefix}.${hasPricingAnnex ? 'noTermsAcceptedWithPricingAnnex' : 'noTermsAccepted'}`,
      );
    }

    const stakeholdersFragment =
      stakeholders && stakeholders.length ? (
        <>
          <SummarySection
            title={t(`${tPrefix}.stakeholders.title`)}
            onEdit={stakeholders.length === 1 ? () => goToStakeholders(stakeholders[0]) : null}
          >
            {stakeholders.map((stakeholder, index) => {
              const { data: { firstName, lastName, dateOfBirth } = {} } = stakeholder;
              return (
                <React.Fragment key={`transaction-summary-merchant-stakeholders-${index}`}>
                  <div
                    {...generateCyElemId(
                      `${STAKEHOLDER_BLOCK}_${firstName}_${lastName}_${dateOfBirth}`,
                      window,
                    )}
                  >
                    <StakeholderSummary
                      merchantCountry={country}
                      stakeholder={stakeholder}
                      t={t}
                      title={t(`${tPrefix}.stakeholders.subtitle`, {
                        index: stakeholders.length > 1 ? index + 1 : '',
                      })}
                      onEdit={
                        stakeholders.length === 1 ? null : () => goToStakeholders(stakeholder)
                      }
                      region={region}
                    />
                  </div>
                  {index + 1 !== stakeholder.length && <SpacerVertical spaceToken="space/150" />}
                </React.Fragment>
              );
            })}
          </SummarySection>
          <SpacerVertical spaceToken="space/600" />
        </>
      ) : null;

    const operatingAddressFragment = operatingAddress && (
      <>
        <AddressSummary
          address={operatingAddress}
          legalName={!address || !isMerchantRegistered ? companyName : undefined}
          onEdit={goToOperatingAddress}
          title={t(
            `${tPrefix}.${isEntityTypePartnership ? 'company' : 'operating'}AddressCard.title`,
          )}
        />
        <SpacerVertical spaceToken="space/600" />
      </>
    );

    return (
      <ContainerTemplate
        currentStepNum={0}
        maxStepNum={0}
        productOptions={[]}
        title={t(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical spaceToken="space/300" />
        {businessDetails && (
          <>
            <BusinessDetailsSummary
              businessDetails={businessDetails}
              booleanAnswer
              country={country}
              onEdit={goToStoreDetails}
            />
            <SpacerVertical spaceToken="space/600" />
          </>
        )}
        {address && isMerchantRegistered && (
          <>
            <AddressSummary
              address={address}
              legalName={companyName}
              onEdit={goToCompanyDetails}
              title={t(`${tPrefix}.companyRegistrationAddressCard.title`)}
            />
            <SpacerVertical spaceToken="space/600" />
          </>
        )}
        {isSoleTraderOrSoleProprietorship ? stakeholdersFragment : operatingAddressFragment}
        {isSoleTraderOrSoleProprietorship ? operatingAddressFragment : stakeholdersFragment}
        {!!bankAccounts.length && (
          <>
            <BankAccountsSummary
              onEdit={country !== 'US' ? goToBankAccounts : undefined}
              country={country}
            />
            <SpacerVertical spaceToken="space/600" />
          </>
        )}
        {hasDocuments && (
          <>
            <DocumentsSummary onEdit={goToDocumentsUpload} region={region} />
            <SpacerVertical spaceToken="space/600" />
          </>
        )}
        {isUnmanagedKyc &&
          productsForAcceptance.map(p => (
            <React.Fragment key={`application-confirmation-merchant-products-${p.id}`}>
              <ProductPricesCard
                isTermsAccepted={isTermsAccepted}
                isPricingAnnexAccepted={isPricingAnnexAccepted}
                region={region}
                countryLabel={t(`products-fe.countries.${country}`)}
                onChangeTermsAcceptance={this.onCheckTermsAndConditions}
                onChangePricingAnnexAcceptance={this.onCheckPricingAnnex}
                onOpenPricing={openPricing}
                onEdit={country !== 'US' ? goToProductSelection : undefined}
                product={p}
                pricingAnnex={pricingAnnex?.[p.id]}
                title={t('products-fe.components.product_prices_card.title')}
                error={errorText}
              />
            </React.Fragment>
          ))}
        <SpacerVertical spaceToken="space/600" />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton
            onClick={this.onNext}
            disabled={isDisabled}
            loading={isLoading}
            {...generateCyElemId(BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.submit`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}
ApplicationConfirmation.propTypes = {
  ...defaultContainerPropTypes,
  canSubmitApplication: PropTypes.bool.isRequired,
  goToBankAccounts: PropTypes.func.isRequired,
  goToCompanyDetails: PropTypes.func.isRequired,
  goToDocumentsUpload: PropTypes.func.isRequired,
  goToOperatingAddress: PropTypes.func.isRequired,
  goToProductSelection: PropTypes.func.isRequired,
  goToStakeholders: PropTypes.func.isRequired,
  goToStoreDetails: PropTypes.func.isRequired,
  hasDocuments: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  isApplicant: PropTypes.func.isRequired,
  isEntityTypePartnership: PropTypes.bool.isRequired,
  isMerchantRegistered: PropTypes.bool.isRequired,
  isSoleTraderOrSoleProprietorship: PropTypes.bool.isRequired,
  isUnmanagedKyc: PropTypes.bool.isRequired,
  merchantData: PropTypes.shape({
    bankAccounts: PropTypes.array.isRequired,
    country: PropTypes.string.isRequired,
    address: PropTypes.object,
    businessDetails: PropTypes.object,
    operatingAddress: PropTypes.object,
    productsForAcceptance: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    stakeholders: PropTypes.array,
  }).isRequired,
  mid: PropTypes.string.isRequired,
  openPricing: PropTypes.func.isRequired,
  region: PropTypes.oneOf(Object.values(REGIONS)).isRequired,
  t: PropTypes.func.isRequired,
  pricingAnnex: PropTypes.objectOf(PropTypes.string),
};

export default compose(
  connect(
    (state, { country }) => ({
      canSubmitApplication: canSubmitApplication(country)(state),
      hasControlOverManagementStakeholders: hasControlOverManagementStakeholders(state),
      hasDocuments: hasDocuments(state),
      isApplicant: isApplicant(state),
      isApplicantOnly: isApplicantOnly(state),
      isEntityTypePartnership: isEntityTypePartnership(state),
      isSoleTraderOrSoleProprietorship: isSoleTraderOrSoleProprietorship(state),
      isManagedKyc: isManagedKyc(state),
      isMerchantRegistered: isMerchantRegistered(state),
      isUnmanagedKyc: isUnmanagedKyc(state),
      merchantData: getDataForApplicationConfirmation(state),
      pricingAnnex: getPricingAnnex(state),
      region: merchantRegion(state),
      t: translator(state),
      tMerchantAware: getMerchantAwareTranslator(state),
    }),
    {},
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      goToCompanyDetails: () => {
        editLogger('company details');
        ownProps.history.push(
          `${ownProps.basePath}/${FLOW_STEP_PATHS.COMPANY_DETAILS_PATH}` +
            ownProps.history.location.search,
        );
      },
      goToOperatingAddress: () => {
        editLogger('operating address');
        const { isEntityTypePartnership, isSoleTraderOrSoleProprietorship, country } = ownProps;

        if (isEntityTypePartnership) {
          ownProps.history.push(
            `${ownProps.basePath}/${FLOW_STEP_PATHS.PARTNERSHIP_DETAILS_PATH}` +
              ownProps.history.location.search,
          );
        } else if (
          ['GB', 'DE', 'AU', 'US', 'IT'].includes(country) &&
          isSoleTraderOrSoleProprietorship
        ) {
          ownProps.history.push(
            `${ownProps.basePath}/${FLOW_STEP_PATHS.SOLE_TRADER_PATH}` +
              ownProps.history.location.search,
          );
        } else {
          ownProps.history.push(
            `${ownProps.basePath}/${FLOW_STEP_PATHS.COMPANY_DETAILS_PATH}` +
              ownProps.history.location.search,
          );
        }
      },
      goToProductSelection: () => {
        editLogger('products');
        ownProps.history.push(
          `${ownProps.basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}` +
            ownProps.history.location.search,
        );
      },
      goToBankAccounts: () => {
        editLogger('bank accounts');
        ownProps.history.push(
          `${ownProps.basePath}/${FLOW_STEP_PATHS.BANK_ACCOUNT_PATH}` +
            ownProps.history.location.search,
        );
      },
      goToStakeholders: stakeholder => {
        editLogger('stakeholders');
        const { isApplicant, isApplicantOnly } = stateProps;
        const {
          basePath,
          country,
          region,
          hasControlOverManagementStakeholders,
          history,
          isEntityTypePartnership,
          isSoleTraderOrSoleProprietorship,
        } = ownProps;

        const editUrl = getStakeholderUrlToRedirect(
          country,
          region,
          {
            hasControlOverManagementStakeholders,
            isApplicant: isApplicant(stakeholder),
            isApplicantOnly: isApplicantOnly(stakeholder),
            isEntityTypePartnership,
            isSoleTraderOrSoleProprietorship,
          },
          basePath,
        );

        history.push(editUrl + history.location.search);
      },
      goToStoreDetails: () => {
        editLogger('store details');
        ownProps.history.push(
          `${ownProps.basePath}/${FLOW_STEP_PATHS.STORE_DETAILS_PATH}` +
            ownProps.history.location.search,
        );
      },
      goToDocumentsUpload: () => {
        editLogger('documents');
        ownProps.history.push(
          `${ownProps.basePath}/${FLOW_STEP_PATHS.DOCUMENTS}` + ownProps.history.location.search,
        );
      },
      openPricing: () => {
        logEvent(ANALYTICS_EVENTS.openPricingTable);
        ownProps.history.push({
          pathname: `${ownProps.basePath}/${APP_PATHS.PRICING_PATH}`,
          search: ownProps.history.location.search,
          state: { referrer: ownProps.location.pathname },
        });
      },
    }),
  ),
  branch(
    ({ isManagedKyc }) => isManagedKyc,
    withChainedCapabilitiesBeforeNext(
      { nextStatus: CAPS.V2_MERCHANTS_NEXT_STATUS_POST, getMerchant: CAPS.V2_MERCHANTS_GET },
      {
        forceCapabilitiesOrder: ['nextStatus', 'getMerchant'],
        callback: analyticLogger,
      },
    ),
    branch(
      ({ isUnmanagedKyc }) => !isUnmanagedKyc,
      withChainedCapabilitiesBeforeNext(
        { nextStatus: CAPS.V2_MERCHANTS_NEXT_STATUS_POST },
        { callback: analyticLogger },
      ),
      withChainedCapabilitiesBeforeNext(
        {
          submitTermsAndConditions: CAPS.V2_MERCHANTS_TERMS_AND_CONDITIONS_POST,
          nextStatus: CAPS.V2_MERCHANTS_NEXT_STATUS_POST,
        },
        {
          forceCapabilitiesOrder: ['submitTermsAndConditions', 'nextStatus'],
          callback: analyticLogger,
        },
      ),
    ),
  ),
)(ApplicationConfirmation);
